import { useEffect, useState } from 'react';
import { http } from '../../lib/api';
import { AppLayout } from '../../components/layout';
import { Button, Card, StatusBadge, Input, Select } from '../../components/core';
import { Modal, SpinnerBlock } from '../../components/feedback';
import { ADMIN_NAV } from './nav';
import { priceForCycle, planCycleOf } from '@livestreamx/shared-types';

interface Plan {
  id: string; name: string; description: string | null;
  priceMonth: number; priceYear: number | null; priceDay: number | null; currency: string;
  billingCycle: 'DAILY' | 'MONTHLY' | 'YEARLY' | null;
  cycleCount: number;
  maxStreams: number; maxDestinationsPerStream: number; maxTotalDestinations: number;
  maxStorageGb: number | null; maxBandwidthGb: number | null;
  recordingEnabled: boolean; maxInboundBitrateMbps: number | null;
  maxOutboundBitrateMbps: number | null;
  viewLimit: number | null;
  status: string; createdAt: string;
}

const fmtP = (n: number) => n.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 });

/** A plan is sold on one cycle for a price that covers the whole period. */
const CYCLES = [
  { value: 'DAILY', label: 'Day' },
  { value: 'MONTHLY', label: 'Month' },
  { value: 'YEARLY', label: 'Year' },
];
/** Label of the duration input ("how many …"), chosen by the selected cycle. */
const COUNT_LABEL: Record<string, string> = { DAILY: 'Days', MONTHLY: 'Months', YEARLY: 'Years' };
/** Singular unit for hints ("total for 3 month(s)"). */
const UNIT: Record<string, string> = { DAILY: 'day', MONTHLY: 'month', YEARLY: 'year' };
const CYCLE_SUFFIX: Record<string, string> = { DAILY: '/day', MONTHLY: '/mo', YEARLY: '/yr' };

function planCycle(p: Plan) {
  // Shared helper: prefers p.billingCycle, infers it from the price fields on
  // plans written before that column existed.
  return planCycleOf(p);
}

/** Price shown/saved belongs to the plan's own cycle and covers the whole period. */
function planPrice(p: Plan): number {
  return priceForCycle(p, planCycleOf(p));
}

/** "/mo" for a single cycle, "/3 months" when one purchase buys several. */
function planPeriod(p: Plan): string {
  const cycle = planCycleOf(p);
  return (p.cycleCount ?? 1) > 1 ? `/${p.cycleCount} ${UNIT[cycle]}s` : CYCLE_SUFFIX[cycle];
}

function PlanCard({ plan, onEdit, onArchive, onActivate, onDelete }: { plan: Plan; onEdit: () => void; onArchive: () => void; onActivate: () => void; onDelete: () => void }) {
  return (
    <Card className={`plan-card ${plan.status === 'ARCHIVED' ? 'archived' : ''}`}>
      <div className="plan-card-head">
        <h3>{plan.name}</h3>
        <StatusBadge status={plan.status} />
      </div>
      <div className="plan-price">
        {fmtP(planPrice(plan))}<small>{planPeriod(plan)}</small>
      </div>
      {plan.description && <p className="text-muted text-sm">{plan.description}</p>}
      <div className="plan-limits">
        <div className="plan-limit"><span className="plan-limit-label">Streams</span><span className="plan-limit-value">{plan.maxStreams}</span></div>
        <div className="plan-limit"><span className="plan-limit-label">Dest/Stream</span><span className="plan-limit-value">{plan.maxDestinationsPerStream}</span></div>
        <div className="plan-limit"><span className="plan-limit-label">Total Dest</span><span className="plan-limit-value">{plan.maxTotalDestinations}</span></div>
        {plan.maxStorageGb != null && <div className="plan-limit"><span className="plan-limit-label">Storage</span><span className="plan-limit-value">{plan.maxStorageGb} GB</span></div>}
        {plan.maxBandwidthGb != null && <div className="plan-limit"><span className="plan-limit-label">Bandwidth</span><span className="plan-limit-value">{plan.maxBandwidthGb} GB</span></div>}
        {plan.maxInboundBitrateMbps != null && <div className="plan-limit"><span className="plan-limit-label">In</span><span className="plan-limit-value">{plan.maxInboundBitrateMbps || '∞'} Mbps</span></div>}
        {plan.maxOutboundBitrateMbps != null && <div className="plan-limit"><span className="plan-limit-label">Out/Dest</span><span className="plan-limit-value">{plan.maxOutboundBitrateMbps || '∞'} Mbps</span></div>}
        {plan.recordingEnabled && <div className="plan-limit"><span className="plan-limit-label">Recording</span><span className="plan-limit-value">✓</span></div>}
      </div>
      <div className="plan-actions">
        <Button variant="secondary" size="sm" onClick={onEdit}>Edit</Button>
        {plan.status === 'ARCHIVED' ? <Button variant="secondary" size="sm" onClick={onActivate}>Activate</Button> : <Button variant="secondary" size="sm" onClick={onArchive}>Archive</Button>}
        <Button variant="danger" size="sm" onClick={onDelete}>Delete</Button>
      </div>
    </Card>
  );
}

const EF = { name: '', desc: '', cycle: 'MONTHLY', count: '1', price: '', streams: '', destPer: '', totalDest: '', storage: '', bandwidth: '', recording: false, bitrate: '', outBitrate: '', viewLimit: '' };

export function AdminPlansPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [f, setF] = useState(EF);
  const [saving, setSaving] = useState(false);

  const load = () => { http.get<Plan[]>('/api/admin/plans').then(setPlans).catch((e) => setError(e.message ?? 'Failed')).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openCreate = () => { setEditId(null); setF(EF); setShow(true); };
  const openEdit = (p: Plan) => {
    setEditId(p.id);
    setF({ name: p.name, desc: p.description ?? '', cycle: planCycle(p), count: String(p.cycleCount ?? 1), price: String(planPrice(p)), streams: String(p.maxStreams),
      destPer: String(p.maxDestinationsPerStream), totalDest: String(p.maxTotalDestinations),
      storage: p.maxStorageGb != null ? String(p.maxStorageGb) : '',
      bandwidth: p.maxBandwidthGb != null ? String(p.maxBandwidthGb) : '',
      recording: p.recordingEnabled, bitrate: p.maxInboundBitrateMbps != null ? String(p.maxInboundBitrateMbps) : '',
      outBitrate: p.maxOutboundBitrateMbps != null ? String(p.maxOutboundBitrateMbps) : '', viewLimit: p.viewLimit != null ? String(p.viewLimit) : '' });
    setShow(true);
  };

  const save = async () => {
    // Name + Price are required by the API (PlanDto). Never bail out
    // silently: a click that produces no request reads as "the plan was not
    // created", and the page-level alert sits *behind* this modal.
    const name = f.name.trim();
    if (!name || !f.price) {
      setError(!name ? 'Plan name is required.' : 'Price is required (use 0 for a free plan).');
      return;
    }
    const cycleCount = Math.floor(Number(f.count) || 0);
    if (cycleCount < 1) {
      setError(`${COUNT_LABEL[f.cycle] ?? 'Duration'} must be at least 1.`);
      return;
    }
    if (saving) return;
    setError('');
    setSaving(true);
    const amount = Number(f.price);
    // One plan = one billing cycle × how many of them one purchase buys (the
    // price covers that whole period). Only that cycle's price field carries
    // the amount — the API requires priceMonth, so it holds the MONTHLY
    // amount and stays 0 otherwise.
    const body = { name, description: f.desc || null,
      billingCycle: f.cycle, cycleCount,
      priceMonth: f.cycle === 'MONTHLY' ? amount : 0,
      priceYear: f.cycle === 'YEARLY' ? amount : null,
      priceDay: f.cycle === 'DAILY' ? amount : null, maxStreams: Number(f.streams) || 0,
      maxDestinationsPerStream: Number(f.destPer) || 0, maxTotalDestinations: Number(f.totalDest) || 0,
      maxStorageGb: f.storage ? Number(f.storage) : null, maxBandwidthGb: f.bandwidth ? Number(f.bandwidth) : null,
      recordingEnabled: f.recording, maxInboundBitrateMbps: f.bitrate ? Number(f.bitrate) : null,
      maxOutboundBitrateMbps: f.outBitrate ? Number(f.outBitrate) : 0,
      viewLimit: f.viewLimit ? Number(f.viewLimit) : null };
    try {
      if (editId) await http.patch('/api/admin/plans/' + editId, body);
      else await http.post('/api/admin/plans', body);
      setShow(false); load();
    } catch (e) { setError((e as Error).message ?? 'Save failed'); }
    setSaving(false);
  };

  const archive = async (id: string) => { try { await http.patch('/api/admin/plans/' + id, { status: 'ARCHIVED' }); load(); } catch (e) { setError((e as Error).message ?? 'Archive failed'); } };

  const activate = async (id: string) => { try { await http.patch('/api/admin/plans/' + id, { status: 'ACTIVE' }); load(); } catch (e) { setError((e as Error).message ?? 'Activate failed'); } };
  const delPlan = async (p: Plan) => {
    if (!window.confirm(`Delete plan "${p.name}"? This cannot be undone.`)) return;
    try { await http.delete('/api/admin/plans/' + p.id); load(); } catch (e) { setError((e as Error).message ?? 'Delete failed'); }
  };

  return (
    <AppLayout brand="LiveStreamX" navGroups={ADMIN_NAV} variant="admin">
      <div className="admin-page">
        <div className="page-header admin-page-header">
          <div><h1 className="page-title">Plans</h1><p className="page-subtitle">Configure subscription plans</p></div>
          <Button onClick={openCreate}>+ New Plan</Button>
        </div>
        {error && <div className="alert danger">{error}</div>}
        {loading ? <SpinnerBlock /> : (
          <div className="plan-grid">
            {!plans.length && <p className="text-muted">No plans yet.</p>}
            {plans.map((p) => <PlanCard key={p.id} plan={p} onEdit={() => openEdit(p)} onArchive={() => archive(p.id)} onActivate={() => activate(p.id)} onDelete={() => delPlan(p)} />)}
          </div>
        )}
        {show && (
          <Modal className="wide" title={editId ? 'Edit Plan' : 'Create Plan'} onClose={() => setShow(false)} footer={<>
            <Button variant="secondary" onClick={() => setShow(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : editId ? 'Save' : 'Create'}</Button>
          </>}>
            {error && <div className="alert danger">{error}</div>}
            {/* Enter anywhere in the form submits — there is no <form> here, so
                without this the keyboard path silently did nothing. */}
            <div className="modal-grid" onKeyDown={(e) => { if (e.key === 'Enter') void save(); }}>
              <Input label="Name" value={f.name} onChange={(v) => setF({ ...f, name: v })} required />
              <Select label="Billing Cycle" value={f.cycle} options={CYCLES} onChange={(v) => setF({ ...f, cycle: v })} hint="Day, Month or Year" />
              <Input label={COUNT_LABEL[f.cycle] ?? 'Months'} type="number" value={f.count} onChange={(v) => setF({ ...f, count: v })} required
                hint={`How many ${UNIT[f.cycle] ?? 'month'}s one purchase covers · 1 = a single ${UNIT[f.cycle] ?? 'month'}`} />
              <Input label="Price" type="number" value={f.price} onChange={(v) => setF({ ...f, price: v })} required
                hint={`Required · total for ${Math.floor(Number(f.count) || 1)} ${UNIT[f.cycle] ?? 'month'}${Math.floor(Number(f.count) || 1) > 1 ? 's' : ''} · 0 = free`} />
              <div className="field full"><Input label="Description" value={f.desc} onChange={(v) => setF({ ...f, desc: v })} /></div>
              <Input label="Max Streams" type="number" value={f.streams} onChange={(v) => setF({ ...f, streams: v })} />
              <Input label="Dest/Stream" type="number" value={f.destPer} onChange={(v) => setF({ ...f, destPer: v })} />
              <Input label="Total Dest" type="number" value={f.totalDest} onChange={(v) => setF({ ...f, totalDest: v })} />
              <Input label="Storage (GB)" type="number" value={f.storage} onChange={(v) => setF({ ...f, storage: v })} />
              <Input label="Bandwidth (GB)" type="number" value={f.bandwidth} onChange={(v) => setF({ ...f, bandwidth: v })} hint="Per month · 0 = unlimited" />
              <Input label="Inbound Bitrate (Mbps)" type="number" value={f.bitrate} onChange={(v) => setF({ ...f, bitrate: v })} hint="Ingest ceiling — a publisher above this is disconnected · 0 = unlimited" />
              <Input label="Outbound Bitrate (Mbps)" type="number" value={f.outBitrate} onChange={(v) => setF({ ...f, outBitrate: v })} hint="Egress ceiling per destination (each destination is re-encoded down to it) · 0 = unlimited" />
              <Input label="Max Viewers" type="number" value={f.viewLimit} onChange={(v) => setF({ ...f, viewLimit: v })} hint="Max concurrent viewers · 0/empty = unlimited" />
              <label className="field full check">
                <input type="checkbox" checked={f.recording} onChange={(e) => setF({ ...f, recording: e.target.checked })} /> Enable Recording
              </label>
            </div>
          </Modal>
        )}
      </div>
    </AppLayout>
  );
}